import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../Layouts/Spinner";
import ReactSelect from "react-select";
import { Card } from "reactstrap";
import * as yup from "yup";
import * as XLSX from "xlsx";
import { getUserList } from "../../../redux/invoice/action";
import { exportAllReports, getAllReports } from "../../../redux/service/action";
import FullScreenLoader from "../../Layouts/FullScreenLoader/FullScreenLoader";
import { getMembershipList } from "../../../redux/membership/action";
import { formatDate, formatDateInput } from "../../Common/Utils/dateUtils";
import {
  formatPurchaseSourceLabel,
  getMembershipPlanLabel,
  getMembershipStatusClass,
  getMembershipStatusLabel,
  getMembershipTermLabel,
  getMembershipTermShort,
} from "../../Common/Utils/adminViewUtils";
import TestingAccountFilter, { getTestingAccountFilterLabel } from "../../Common/TestingAccountFilter";
import "../../Common/Styles/AdminEnhancements.css";

export const invoiceReportValidation = yup
  .object()
  .shape({
    start_date: yup.date().nullable(),
    end_date: yup
      .date()
      .nullable()
      .min(yup.ref("start_date"), "End date cannot be before start date"),
    user_id: yup.string().nullable(),
    membership_id: yup.string().nullable(),
    status: yup.string().nullable(),
    testing_account_filter: yup.string().nullable(),
  })
  .test("at-least-one", "At least one field is required", (values) => {
    const hasValue = [
      values.start_date,
      values.end_date,
      values.user_id?.trim(),
      values.membership_id?.trim(),
      values.status?.toString()?.trim(),
      values.testing_account_filter !== "hide" ? values.testing_account_filter : "",
    ].some((field) => field !== null && field !== undefined && field !== "");

    return hasValue;
  });

const MemberShipReport = () => {
  const dispatch = useDispatch();
  const [endDates, setEndDates] = useState("");
  const [loading, setLoading] = useState(false);
  const [startDates, setStartDates] = useState("");
  const [reportsData, setReportsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [exportLoader, setExportLoader] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);

  const [theFilterInfo, setTheFilterInfo] = useState({
    user_id: "",
    start_date: "",
    end_date: "",
    membership_id: "",
    status: "",
    testing_account_filter: "hide",
    type: "membership_report",
  });

  const userList = useSelector((state) => state?.reusableSlice?.userList);
  const membershipList = useSelector(
    (state) => state?.reusableSlice?.membershipList
  );

  const statusOptions = [
    { value: 1, label: "Active" },
    { value: 2, label: "Expired" },
  ];

  const companyOptions = userList?.users?.map((item) => ({
    value: item?.id,
    label: item?.company_name,
  }));

  const membershipOptions = membershipList?.memberships?.map((item) => ({
    value: item?.id,
    label: `${item?.title} (${getMembershipPlanLabel(item)})`,
  }));

  useEffect(() => {
    dispatch(getUserList({ search_key: "" }));
    dispatch(getMembershipList({ search_key: "" }));
  }, [dispatch]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_id: "",
      start_date: "",
      end_date: "",
      membership_id: "",
      status: "",
      testing_account_filter: "hide",
      type: "membership_report",
    },
    validationSchema: invoiceReportValidation,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      if (
        values.user_id ||
        values.membership_id ||
        values.status ||
        values.start_date ||
        values.end_date ||
        values.testing_account_filter !== "hide"
      ) {
        handleSubmit(values);
      }
    },
  });

  const handleGetReports = (data) => {
    getAllReports(data)
      .then((res) => {
        if (res.data.status) {
          setIsLoading(false);
          setReportsData(res.data.response?.membership_report || []);
        }
      })
      .catch((e) => {
        console.log(e, "error in get report");
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);
    handleGetReports({ type: "membership_report", testing_account_filter: "hide" });
  }, []);

  const handleSubmit = async (values) => {
    setIsLoading(true);
    setTheFilterInfo(values);
    handleGetReports({ ...values });
  };

  const handleExportReport = () => {
    setExportLoader(true);
    exportAllReports(theFilterInfo)
      .then((response) => {
        if (response.data) {
          setExportLoader(false);
          const header = [[
            "Sl",
            "Company",
            "Member ID",
            "Membership",
            "Term",
            "Source",
            "Price",
            "Simplified Total",
            "Simplified Left",
            "VAT Total",
            "VAT Left",
            "Purchase Total",
            "Purchase Left",
            "Start Date",
            "End Date",
            "Status",
          ]];

          const data = (response.data.membership_report || []).map((prop, index) => [
            index + 1,
            prop.company_name,
            prop.id,
            `${prop.membership_title} (${getMembershipTermShort(prop)})`,
            getMembershipTermLabel(prop),
            formatPurchaseSourceLabel(prop.purchase_source_label),
            prop.price,
            prop.max_invoices,
            prop.invoice_left,
            prop.max_invoices1,
            prop.invoice_left1,
            prop.max_invoices2,
            prop.invoice_left2,
            formatDateInput(prop.start_date),
            formatDateInput(prop.end_date),
            getMembershipStatusLabel(prop.status),
          ]);

          const worksheet = XLSX.utils.aoa_to_sheet(header);
          XLSX.utils.sheet_add_aoa(worksheet, data, { origin: "A2" });

          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
          XLSX.writeFile(workbook, "MembershipReport.xlsx");
        }
      })
      .catch((e) => {
        console.log(e, "while exporting reports");
        setExportLoader(false);
      });
  };

  const handleClearFilter = () => {
    setSelectedCompany(null);
    setSelectedStatus(null);
    setStartDates("");
    setEndDates("");
    setSelectedMembership(null);
    setTheFilterInfo({
      user_id: "",
      start_date: "",
      end_date: "",
      membership_id: "",
      status: "",
      testing_account_filter: "hide",
      type: "membership_report",
    });
    formik.resetForm();
    setIsLoading(true);
    handleGetReports({ type: "membership_report", testing_account_filter: "hide" });
  };

  const summary = useMemo(() => {
    const revenue = reportsData.reduce(
      (sum, item) => sum + Number(item?.price || 0),
      0
    );
    const active = reportsData.filter((item) => Number(item?.status) === 1).length;
    const expired = reportsData.filter((item) => Number(item?.status) !== 1).length;
    const shopify = reportsData.filter(
      (item) => formatPurchaseSourceLabel(item?.purchase_source_label) === "Shopify"
    ).length;

    return [
      { label: "Visible records", value: reportsData.length, subtext: "Rows currently shown" },
      { label: "Active memberships", value: active, subtext: "Currently active" },
      { label: "Expired memberships", value: expired, subtext: "Need renewal" },
      { label: "Visible revenue", value: revenue.toFixed(2), subtext: "Sum of prices on screen" },
      { label: "Shopify purchases", value: shopify, subtext: "Rows sourced from Shopify" },
    ];
  }, [reportsData]);

  const activeFilterChips = [
    selectedCompany?.label && { label: "Company", value: selectedCompany.label },
    selectedMembership?.label && { label: "Membership", value: selectedMembership.label },
    selectedStatus?.label && { label: "Status", value: selectedStatus.label },
    formik.values.testing_account_filter !== "hide" && {
      label: "Account Type",
      value: getTestingAccountFilterLabel(formik.values.testing_account_filter),
    },
    startDates && { label: "Start", value: formatDate(startDates) },
    endDates && { label: "End", value: formatDate(endDates) },
  ].filter(Boolean);

  return (
    <Card>
      <div className="p-3">
        <div className="category-wrapper">
          <div className="admin-toolbar">
            <div>
              <h4 className="mb-1">Membership Report</h4>
              <div className="admin-toolbar-note">
                Clearer terms, sources, date formatting, and quick summaries for the admin team.
              </div>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <button
                type="button"
                className="submit-btn filter-btn mx-5"
                onClick={() => setShowFilter(!showFilter)}
              >
                <span>
                  <i className="fa-solid fa-filter"></i>{" "}
                  {showFilter ? "Hide Filter" : "Filter"}
                </span>
              </button>
              <button
                disabled={exportLoader}
                type="button"
                className="submit-btn"
                onClick={handleExportReport}
                style={{ width: "max-content" }}
              >
                {exportLoader ? <Spinner /> : "Export"}
              </button>
            </div>
          </div>

          <div className="admin-summary-grid">
            {summary.map((item) => (
              <div className="admin-summary-card" key={item.label}>
                <div className="admin-summary-label">{item.label}</div>
                <div className="admin-summary-value">{item.value}</div>
                <div className="admin-summary-subtext">{item.subtext}</div>
              </div>
            ))}
          </div>

          {activeFilterChips.length > 0 && (
            <div className="admin-meta-row">
              {activeFilterChips.map((chip) => (
                <div className="admin-chip" key={`${chip.label}-${chip.value}`}>
                  <strong>{chip.label}:</strong> {chip.value}
                </div>
              ))}
              <button
                type="button"
                className="submit-btn"
                onClick={handleClearFilter}
                style={{ width: "max-content" }}
              >
                Reset All Filters
              </button>
            </div>
          )}

          {showFilter && (
            <form onSubmit={formik.handleSubmit}>
              <div className="row">
                <div className="col-md-3 form-group">
                  <label>Select Company</label>
                  <ReactSelect
                    isClearable
                    value={selectedCompany}
                    onChange={(selected) => {
                      setSelectedCompany(selected || null);
                      formik.setFieldValue("user_id", selected?.value || "");
                    }}
                    options={companyOptions}
                    placeholder="Select Company"
                  />
                  {formik.touched.user_id && formik.errors.user_id ? (
                    <div className="error">{formik.errors.user_id}</div>
                  ) : null}
                </div>
                <div className="col-md-3 form-group">
                  <label>Select Membership</label>
                  <ReactSelect
                    isClearable
                    value={selectedMembership}
                    onChange={(selected) => {
                      setSelectedMembership(selected || null);
                      formik.setFieldValue("membership_id", selected?.value || "");
                    }}
                    options={membershipOptions}
                    placeholder="Select membership"
                  />
                  {formik.touched.membership_id && formik.errors.membership_id ? (
                    <div className="error">{formik.errors.membership_id}</div>
                  ) : null}
                </div>

                <div className="col-md-3 form-group">
                  <label>Select Status</label>
                  <ReactSelect
                    isClearable
                    name="status"
                    id="status"
                    value={selectedStatus}
                    onChange={(selected) => {
                      setSelectedStatus(selected || null);
                      formik.setFieldValue("status", selected?.value || "");
                    }}
                    options={statusOptions}
                    placeholder="Select status"
                  />
                </div>

                <div className="col-md-3 form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    className="form-control w-100"
                    placeholder="Enter start date"
                    value={startDates}
                    onChange={(event) => {
                      setStartDates(event.target.value);
                      formik.setFieldValue("start_date", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    name="start_date"
                  />
                  {formik.touched.start_date && formik.errors.start_date ? (
                    <div className="error">{formik.errors.start_date}</div>
                  ) : null}
                </div>

                <div className="col-md-3 form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    className="form-control w-100"
                    placeholder="Enter end date"
                    value={endDates}
                    onChange={(event) => {
                      setEndDates(event.target.value);
                      formik.setFieldValue("end_date", event.target.value);
                    }}
                    onBlur={formik.handleBlur}
                    min={startDates || undefined}
                    name="end_date"
                  />
                  {formik.touched.end_date && formik.errors.end_date ? (
                    <div className="error">{formik.errors.end_date}</div>
                  ) : null}
                </div>
                <TestingAccountFilter
                  value={formik.values.testing_account_filter}
                  onChange={formik.handleChange}
                />
                <div
                  className="d-flex align-items-center justify-content-center mt-3 col-md-12"
                  style={{ columnGap: "20px" }}
                >
                  <button
                    className="submit-btn"
                    style={{ width: "max-content" }}
                    type="submit"
                  >
                    {!loading ? "Apply Filters" : <Spinner />}
                  </button>
                  <button
                    type="button"
                    className="submit-btn"
                    onClick={handleClearFilter}
                    style={{ width: "max-content" }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
      {isLoading ? (
        <FullScreenLoader />
      ) : (
        <div className="table-sec px-3">
          <table className="table border-table-sec">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">Company</th>
                <th scope="col-1">Membership</th>
                <th scope="col">Source</th>
                <th scope="col">Price</th>
                <th scope="col" colSpan="2" className="text-center">
                  Simplified
                </th>
                <th scope="col" colSpan="2" className="text-center">
                  VAT
                </th>
                <th scope="col" colSpan="2" className="text-center">
                  Purchase
                </th>
                <th scope="col">Start Date</th>
                <th scope="col">End Date</th>
                <th>Status</th>
              </tr>
              <tr>
                <th colSpan="4"></th>
                <th></th>
                <th>Total</th>
                <th>Left</th>
                <th>Total</th>
                <th>Left</th>
                <th>Total</th>
                <th>Left</th>
                <th colSpan="3"></th>
              </tr>
            </thead>
            <tbody>
              {reportsData && reportsData.length > 0 ? (
                reportsData.map((item, index) => (
                  <tr key={item?.id || index}>
                    <td>{index + 1}</td>
                    <td>{item.company_name}</td>
                    <td>
                      {item?.membership_title} ({getMembershipTermShort(item)})
                      <div className="admin-inline-note">{getMembershipTermLabel(item)}</div>
                    </td>
                    <td>
                      <span className="admin-chip">{formatPurchaseSourceLabel(item?.purchase_source_label)}</span>
                    </td>
                    <td>{item.price}</td>
                    <td>{item?.max_invoices}</td>
                    <td>{item?.invoice_left}</td>
                    <td>{item?.max_invoices1}</td>
                    <td>{item?.invoice_left1}</td>
                    <td>{item?.max_invoices2}</td>
                    <td>{item?.invoice_left2}</td>
                    <td>{formatDate(item?.start_date)}</td>
                    <td>{formatDate(item?.end_date)}</td>
                    <td>
                      <span className={`admin-chip admin-chip--${getMembershipStatusClass(item?.status)}`}>
                        {getMembershipStatusLabel(item?.status)}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="14" className="text-center">
                    Membership Reports are Not Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default MemberShipReport;
